import { useState, createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import { Sidebar } from './Sidebar';

interface AppShellContextType {
  toggleSidebar: () => void;
}

const AppShellContext = createContext<AppShellContextType | null>(null);

export function useAppShell() {
  const context = useContext(AppShellContext);
  if (!context) {
    throw new Error('useAppShell must be used within an AppShell');
  }
  return context;
}

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  return (
    <AppShellContext.Provider value={{ toggleSidebar }}>
      <div className="flex h-screen overflow-hidden bg-[var(--color-surface)]">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        {/* Mobile overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-slate-900/50 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
        <div className="flex flex-1 flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto outline-none p-0">{children}</main>
        </div>
      </div>
    </AppShellContext.Provider>
  );
}
